import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import KretaraLogo from "../components/KretaraLogo";
import { api, formatApiError } from "../lib/api";
import { Sparkles, Shield, Workflow, BarChart3 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [mode, setMode] = useState("login"); // login | forgot
  const [forgotEmail, setForgotEmail] = useState("");

  const validateField = (name, value) => {
    if (!value) return "Required";
    if (name === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const eErr = validateField("email", email);
    const pErr = validateField("password", password);
    if (eErr || pErr) {
      setErrors({ email: eErr, password: pErr });
      return;
    }
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const forgot = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("If this email exists, a reset link will be sent.");
      setMode("login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 k-aurora-light dark:k-aurora">
      {/* Hero panel */}
      <div className="hidden lg:flex relative k-aurora text-white p-12 flex-col justify-between overflow-hidden">
        <div className="relative z-10">
          <KretaraLogo size={56} light />
        </div>
        <div className="relative z-10 space-y-8">
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl xl:text-5xl font-bold tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Procurement, refined.
            <br />
            <span className="text-[#1D9E75]">Decisions, intelligent.</span>
          </motion.h1>
          <p className="text-white/70 text-lg max-w-md">
            Multi-level approvals, vendor performance, inventory truth — one premium workspace built
            for retail teams.
          </p>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            {[
              { i: Workflow, t: "Multi-step approvals" },
              { i: Shield, t: "Role-based access" },
              { i: BarChart3, t: "Live analytics" },
              { i: Sparkles, t: "AI-ready data" },
            ].map(({ i: I, t }) => (
              <div key={t} className="k-glass rounded-xl p-4 flex items-center gap-3 bg-white/5 border-white/10">
                <I size={18} className="text-[#1D9E75]" />
                <span className="text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-white/40">© Kretara.ai — Procurement Intelligence</div>
        <div className="k-grain absolute inset-0" />
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8 flex justify-center">
            <KretaraLogo size={64} />
          </div>
          <div className="k-glass-strong rounded-3xl p-8 shadow-xl">
            {mode === "login" ? (
              <>
                <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Welcome back
                </h2>
                <p className="text-sm text-muted-foreground mt-1">Sign in to your Kretara workspace</p>

                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => setErrors((p) => ({ ...p, email: validateField("email", email) }))}
                      placeholder="you@company.com"
                      autoComplete="email"
                      data-testid="login-email"
                    />
                    {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => setErrors((p) => ({ ...p, password: validateField("password", password) }))}
                      autoComplete="current-password"
                      data-testid="login-password"
                    />
                    {errors.password && <p className="text-xs text-destructive mt-1">{errors.password}</p>}
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setMode("forgot")}
                      className="text-xs text-[#534AB7] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Button type="submit" className="w-full bg-[#534AB7] hover:bg-[#463E9F]" disabled={loading} data-testid="login-submit">
                    {loading ? "Signing in..." : "Sign in"}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-border">
                  <div className="text-xs text-muted-foreground mb-2">Try a demo account:</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {[
                      ["Admin", "admin@kretara.ai", "Admin@123"],
                      ["Manager", "manager@kretara.ai", "Manager@123"],
                      ["Officer", "officer@kretara.ai", "Officer@123"],
                      ["Viewer", "viewer@kretara.ai", "Viewer@123"],
                    ].map(([role, e, p]) => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => { setEmail(e); setPassword(p); }}
                        className="text-left p-2 rounded-lg hover:bg-foreground/5 border border-border transition"
                        data-testid={`demo-${role.toLowerCase()}`}
                      >
                        <div className="font-medium">{role}</div>
                        <div className="text-muted-foreground truncate">{e}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Reset password
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your email and we'll send you a reset link.
                </p>
                <form onSubmit={forgot} className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="fe">Email</Label>
                    <Input id="fe" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} data-testid="forgot-email" />
                  </div>
                  <Button type="submit" className="w-full bg-[#534AB7] hover:bg-[#463E9F]" data-testid="forgot-submit">
                    Send reset link
                  </Button>
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="w-full text-sm text-muted-foreground hover:text-foreground"
                  >
                    Back to sign in
                  </button>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
